import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getAllPartnerChargerTransactionDetails } from '../services/partner-service';
import { isSuccessful } from '../services/api-requests';
import { ChargerAttributes } from '../models/charger-model';

dayjs.extend(utc);

export interface InsightsFilters {
  startDate: string; // ISO YYYY-MM-DD
  endDate: string;   // ISO YYYY-MM-DD
  chargerIds?: string[]; // empty or undefined means all
}

export interface DailyKwhPoint {
  date: string; // YYYY-MM-DD
  totalKwh: number;
}

export interface EarningsPoint {
  date: string; // YYYY-MM-DD
  accumulated: number; // cumulative sum
}

export interface MeanderEarningsPoint {
  date: string; // YYYY-MM-DD
  accumulated: number; // cumulative meander partner earnings
}

export interface SplitEarningsPoint {
  date: string; // YYYY-MM-DD
  accumulated: number; // cumulative split revenue share earnings
}

export interface PartnerEarningsPoint {
  date: string; // YYYY-MM-DD
  amount: number; // earnings for that day (not accumulated)
}

export interface EarningsSummary {
  hasMeanderPartner: boolean;
  hasRevenueSplit: boolean;
  meanderPartnerTotal: number;
  revenueSplitTotal: number;
  combinedTotal: number;
  currentSplitPercentage?: number;
  splitStatus?: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'REVOKED';
}

export interface ActiveBreakdown {
  activeCount: number;
  inactiveCount: number;
}

export interface PartnerInsightsData {
  dailyKwh: DailyKwhPoint[];
  accumulatedEarnings: EarningsPoint[]; // Combined/legacy
  accumulatedMeanderEarnings: MeanderEarningsPoint[];
  accumulatedSplitEarnings: SplitEarningsPoint[];
  partnerEarnings: PartnerEarningsPoint[];
  breakdown: ActiveBreakdown;
  lifetimeSums: { partnerEarnings: number, allKwh: number };
  earningsSummary: EarningsSummary;
}

const toIsoDay = (d: Date) => d.toISOString().split('T')[0];

const buildDateBuckets = (startIso: string, endIso: string) => {
  const start = dayjs(startIso).startOf('day');
  const end = dayjs(endIso).startOf('day');
  const buckets: string[] = [];
  let curr = start;
  while (curr.isBefore(end) || curr.isSame(end)) {
    buckets.push(curr.format('YYYY-MM-DD'));
    curr = curr.add(1, 'day');
  }
  return buckets;
};

export function usePartnerInsightsData(filters?: Partial<InsightsFilters>) {
  const defaultStart = useMemo(() => dayjs().subtract(1, 'month').format('YYYY-MM-DD'), []);
  const defaultEnd = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const [selectedStartDate, setSelectedStartDate] = useState<string>(filters?.startDate || defaultStart);
  const [selectedEndDate, setSelectedEndDate] = useState<string>(filters?.endDate || defaultEnd);
  const [selectedChargerIds, setSelectedChargerIds] = useState<string[] | undefined>(filters?.chargerIds);

  const [data, setData] = useState<PartnerInsightsData>({ 
    dailyKwh: [], 
    accumulatedEarnings: [], 
    accumulatedMeanderEarnings: [],
    accumulatedSplitEarnings: [],
    partnerEarnings: [],
    breakdown: { activeCount: 0, inactiveCount: 0 },
    lifetimeSums: { partnerEarnings: 0, allKwh: 0 },
    earningsSummary: {
      hasMeanderPartner: false,
      hasRevenueSplit: false,
      meanderPartnerTotal: 0,
      revenueSplitTotal: 0,
      combinedTotal: 0,
    },
  });
  const [chargers, setChargers] = useState<ChargerAttributes[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Convert local date strings to UTC timestamps
      // Special handling for end date: if current UTC date is ahead of selected end date,
      // extend by one day to capture transactions that occurred "today" locally but "tomorrow" in UTC
      const startDateUtc = dayjs.utc(selectedStartDate).startOf('day');
      let endDateUtc = dayjs.utc(selectedEndDate).endOf('day');
      
      // Check if we need to extend end date
      const currentLocalDate = dayjs().format('YYYY-MM-DD');
      const currentUtcDate = dayjs.utc().format('YYYY-MM-DD');
      
      // If the selected end date is "today" locally, but UTC has rolled to tomorrow,
      // extend the end date by one day to capture all of today's local transactions
      if (selectedEndDate === currentLocalDate && currentUtcDate > currentLocalDate) {
        endDateUtc = endDateUtc.add(1, 'day');
      }
      
      const resp = await getAllPartnerChargerTransactionDetails({
        startDate: startDateUtc.toISOString(),
        endDate: endDateUtc.toISOString(),
      });

      if (!isSuccessful(resp.status) || !resp.payload) {
        throw new Error('Failed to load insights');
      }

      const list = resp.payload.chargers || [];
      const earnings = resp.payload.earnings;
      const earningsTrend = resp.payload.earningsTrend || [];

      // Extract chargers and attach permissions from the parent object
      setChargers(list.map(c => ({
        ...c.charger,
        permissions: c.permissions as Array<'read' | 'write' | 'meander' | 'split' | 'observer'>,
      })));

      // Filter by chargerIds - if none selected, use empty array (show zeros)
      const chargerIdSet = selectedChargerIds && selectedChargerIds.length > 0 ? new Set(selectedChargerIds) : null;
      const filtered = chargerIdSet ? list.filter(c => chargerIdSet.has(c.charger?.uuid || c.chargerId)) : [];

      // Use the same extended end date for building buckets as we used for the API call
      let endDateForBuckets = selectedEndDate;
      if (selectedEndDate === currentLocalDate && currentUtcDate > currentLocalDate) {
        endDateForBuckets = dayjs(selectedEndDate).add(1, 'day').format('YYYY-MM-DD');
      }
      
      const dayKeys = buildDateBuckets(selectedStartDate, endDateForBuckets);

      // Check if we have new earnings data structure
      const hasNewEarningsStructure = !!earnings;

      let accumulatedMeanderEarnings: MeanderEarningsPoint[] = [];
      let accumulatedSplitEarnings: SplitEarningsPoint[] = [];
      let accumulatedEarnings: EarningsPoint[] = [];
      let partnerEarnings: PartnerEarningsPoint[] = [];
      let earningsSummary: EarningsSummary = {
        hasMeanderPartner: false,
        hasRevenueSplit: false,
        meanderPartnerTotal: 0,
        revenueSplitTotal: 0,
        combinedTotal: 0,
      };

      if (hasNewEarningsStructure && earnings) {
        // Use new earnings structure
        const hasMeanderPartner = !!earnings.meanderPartner;
        const hasRevenueSplit = !!earnings.revenueSplit;

        earningsSummary = {
          hasMeanderPartner,
          hasRevenueSplit,
          meanderPartnerTotal: earnings.meanderPartner?.currentPeriod?.total || 0,
          revenueSplitTotal: earnings.revenueSplit?.currentPeriod?.total || 0,
          combinedTotal: earnings.combined?.currentPeriodTotal || 0,
          currentSplitPercentage: earnings.revenueSplit?.currentSplitPercentage,
          splitStatus: earnings.revenueSplit?.splitStatus,
        };

        // Build accumulated earnings from trend data
        if (earningsTrend.length > 0) {
          let meanderRunning = 0;
          let splitRunning = 0;
          let combinedRunning = 0;

          // Convert monthly trend to daily format for the selected date range
          const trendByMonth = new Map(earningsTrend.map(t => [t.month, t]));
          
          accumulatedMeanderEarnings = dayKeys.map(d => {
            const month = d.substring(0, 7); // Get YYYY-MM
            const trend = trendByMonth.get(month);
            if (trend && dayjs(d).date() === 1) { // First day of month
              meanderRunning += trend.meanderPartnerEarnings;
            }
            return { date: d, accumulated: +meanderRunning.toFixed(2) };
          });

          accumulatedSplitEarnings = dayKeys.map(d => {
            const month = d.substring(0, 7);
            const trend = trendByMonth.get(month);
            if (trend && dayjs(d).date() === 1) {
              splitRunning += trend.revenueSplitEarnings;
            }
            return { date: d, accumulated: +splitRunning.toFixed(2) };
          });

          accumulatedEarnings = dayKeys.map(d => {
            const month = d.substring(0, 7);
            const trend = trendByMonth.get(month);
            if (trend && dayjs(d).date() === 1) {
              combinedRunning += trend.totalEarnings;
            }
            return { date: d, accumulated: +combinedRunning.toFixed(2) };
          });

          // Build daily partner earnings (combined)
          partnerEarnings = dayKeys.map(d => ({ date: d, amount: 0 }));
        }
      } else {
        // Fallback to legacy calculation (for backward compatibility)
        const kwhPerDay = new Map<string, number>(dayKeys.map(d => [d, 0]));
        const meanderEarningsPerDay = new Map<string, number>(dayKeys.map(d => [d, 0]));
        const splitEarningsPerDay = new Map<string, number>(dayKeys.map(d => [d, 0]));

        let hasMeanderEarnings = false;
        let hasSplitEarnings = false;

        filtered.forEach(entry => {
          const hist = entry.chargerHistory || [];
          hist.forEach(h => {
            const dateRaw = h?.charging_end_time;
            if (!dateRaw) return;
            const d = new Date(dateRaw);
            if (isNaN(d.getTime())) return;
            const key = toIsoDay(d);
            if (!kwhPerDay.has(key)) return;

            const kwh = Number(h?.total_kwh) || 0;
            kwhPerDay.set(key, (kwhPerDay.get(key) || 0) + kwh);
          });

          const orders = (entry as any)?.orders || [];
          orders.forEach((o: any) => {
            const when = o?.updatedAt || o?.updated_at || o?.updated || o?.updated_on;
            if (!when) return;
            const d = new Date(when);
            if (isNaN(d.getTime())) return;
            const key = toIsoDay(d);
            if (!meanderEarningsPerDay.has(key)) return;

            // Meander partner earnings (transaction fees, kwh markup, discounts)
            const tx = Number(o?.partner_transaction_fee_applied || 0);
            const kwh = Number(o?.partner_kwh_markup_fee_applied || 0);
            const disc = Number(o?.partner_discount_applied || 0);
            const meanderDelta = tx + kwh - disc;
            
            if (meanderDelta !== 0) {
              hasMeanderEarnings = true;
              meanderEarningsPerDay.set(key, (meanderEarningsPerDay.get(key) || 0) + meanderDelta);
            }

            // Split revenue share earnings (from partner_split_amount)
            const splitAmount = Number(o?.partner_split_amount || 0);
            if (splitAmount !== 0) {
              hasSplitEarnings = true;
              splitEarningsPerDay.set(key, (splitEarningsPerDay.get(key) || 0) + splitAmount);
            }
          });
        });

        // Build separate accumulated earnings
        let meanderRunning = 0;
        accumulatedMeanderEarnings = dayKeys.map(d => {
          meanderRunning += +(meanderEarningsPerDay.get(d) || 0);
          return { date: d, accumulated: +meanderRunning.toFixed(2) };
        });

        let splitRunning = 0;
        accumulatedSplitEarnings = dayKeys.map(d => {
          splitRunning += +(splitEarningsPerDay.get(d) || 0);
          return { date: d, accumulated: +splitRunning.toFixed(2) };
        });

        // Build combined accumulated earnings
        let combinedRunning = 0;
        accumulatedEarnings = dayKeys.map(d => {
          const meanderDaily = meanderEarningsPerDay.get(d) || 0;
          const splitDaily = splitEarningsPerDay.get(d) || 0;
          combinedRunning += meanderDaily + splitDaily;
          return { date: d, accumulated: +combinedRunning.toFixed(2) };
        });

        // Build daily partner earnings (combined for backward compatibility)
        partnerEarnings = dayKeys.map(d => {
          const meanderDaily = meanderEarningsPerDay.get(d) || 0;
          const splitDaily = splitEarningsPerDay.get(d) || 0;
          return { 
            date: d, 
            amount: +((meanderDaily + splitDaily).toFixed(2)) 
          };
        });
        
        earningsSummary = {
          hasMeanderPartner: hasMeanderEarnings,
          hasRevenueSplit: hasSplitEarnings,
          meanderPartnerTotal: +meanderRunning.toFixed(2),
          revenueSplitTotal: +splitRunning.toFixed(2),
          combinedTotal: +combinedRunning.toFixed(2),
        };
      }

      // aggregate daily kWh (sum across chargers per day)
      const kwhPerDay = new Map<string, number>(dayKeys.map(d => [d, 0]));
      
      filtered.forEach(entry => {
        const hist = entry.chargerHistory || [];
        hist.forEach(h => {
          const dateRaw = h?.charging_end_time;
          if (!dateRaw) return;
          const d = new Date(dateRaw);
          if (isNaN(d.getTime())) return;
          const key = toIsoDay(d);
          if (!kwhPerDay.has(key)) return;

          const kwh = Number(h?.total_kwh) || 0;
          kwhPerDay.set(key, (kwhPerDay.get(key) || 0) + kwh);
        });
      });

      const dailyKwh = dayKeys.map(d => ({ date: d, totalKwh: +(kwhPerDay.get(d) || 0) }));

      // active vs inactive breakdown using empty chargerHistory arrays
      const totalChargers = list.length;
      const inactiveCount = list.reduce((count, entry) => {
        const hist = entry?.chargerHistory;
        return count + (Array.isArray(hist) && hist.length === 0 ? 1 : 0);
      }, 0);
      const activeCount = Math.max(0, totalChargers - inactiveCount);

      // Calculate lifetime sums from filtered chargers
      // Use the new earnings.combined.lifetimeTotal structure
      const lifetimeSums = filtered.reduce((acc, entry) => {
        return {
          partnerEarnings: acc.partnerEarnings + (Number(entry.earnings?.combined?.lifetimeTotal) || 0),
          allKwh: acc.allKwh + (Number(entry.lifetimeKwh) || 0),
        };
      }, { partnerEarnings: 0, allKwh: 0 });

      setData({
        dailyKwh,
        accumulatedEarnings,
        accumulatedMeanderEarnings,
        accumulatedSplitEarnings,
        partnerEarnings,
        breakdown: { activeCount, inactiveCount },
        lifetimeSums,
        earningsSummary,
      });
    } catch (e: any) {
      setError(e?.message || 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [selectedStartDate, selectedEndDate, selectedChargerIds]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return {
    data,
    chargers,
    loading,
    error,
    setSelectedStartDate,
    setSelectedEndDate,
    setSelectedChargerIds,
    selectedStartDate,
    selectedEndDate,
    selectedChargerIds,
    refetch,
  };
}


